import { useCallback, useMemo, useState } from "react";

import type { BlackboardChannel } from "../blackboardModels";

import {
  type ChannelEnvironmentFormValues,
  type ChannelEnvironmentPatchResponse,
  type ResumeUnavailableRefusal,
  ResumeUnavailableError,
  useChannelEnvironment,
} from "../useChannelEnvironment";
import {
  type ChannelWorkspaceRepository,
  type ChannelWorkspaceRepositoryRefreshResponse,
  useChannelWorkspaceRepositories,
} from "../useChannelWorkspaceRepositories";
import {
  formatRepoCount,
  getStatusLabel,
  getStatusTone,
} from "./environmentUtils";
import { selectActiveRepositories } from "./Repositories";

export type WorkspaceDialogState =
  | { kind: "none" }
  | { kind: "attach" }
  | { kind: "edit" }
  | {
      kind: "patch-confirm";
      message: string;
      response: ChannelEnvironmentPatchResponse;
      title: string;
      values: ChannelEnvironmentFormValues;
    }
  | { kind: "archive-confirm" }
  | { kind: "detach-confirm" }
  | { kind: "attach-repo" }
  | { kind: "change-branch"; repository: ChannelWorkspaceRepository }
  | { kind: "detach-repo"; repository: ChannelWorkspaceRepository }
  | {
      kind: "acknowledge-absent-detach";
      repository: ChannelWorkspaceRepository;
    }
  | { kind: "refresh-repo"; repository: ChannelWorkspaceRepository }
  | { kind: "restart-confirm" };

export async function restartWorkspaceAndRefreshRepositories(
  restart: () => Promise<void>,
  refreshRepositoriesAfterRestart: () => Promise<void>,
): Promise<void> {
  await restart();
  await refreshRepositoriesAfterRestart();
}

export function useWorkspaceController(channel: BlackboardChannel) {
  const {
    archive,
    attach,
    detach,
    environment,
    error,
    launchPreview,
    loading,
    patch,
    profiles,
    profilesError,
    profilesLoading,
    refresh,
    restart,
    start,
  } = useChannelEnvironment(channel.id);
  const {
    acknowledgeAbsentDetach: acknowledgeAbsentRepositoryDetach,
    attach: attachRepository,
    changeBranch: changeRepositoryBranch,
    detach: detachRepository,
    error: repositoriesError,
    refresh: refreshRepositories,
    refreshAfterRestart: refreshRepositoriesAfterRestart,
    refreshRepository,
    repositories,
  } = useChannelWorkspaceRepositories(channel.id, {
    enabled: Boolean(environment),
  });
  const [dialog, setDialog] = useState<WorkspaceDialogState>({ kind: "none" });
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // The thrown value behind `actionError`, for dialogs that offer typed
  // follow-ups (e.g. branch-collision choices) instead of flat text.
  const [actionErrorCause, setActionErrorCause] = useState<unknown>(null);
  const [resumeRefusal, setResumeRefusal] =
    useState<ResumeUnavailableRefusal | null>(null);
  // The action that was refused, so the modal's "Retry" re-runs the operation
  // that actually failed (start/resume/attach), not a hard-coded one.
  const [resumeRetry, setResumeRetry] = useState<{
    label: string;
    run: () => Promise<void>;
  } | null>(null);
  const [refreshOverride, setRefreshOverride] = useState<{
    reason: string | null;
    repositoryId: string;
  } | null>(null);
  const [refreshResult, setRefreshResult] = useState<
    ChannelWorkspaceRepositoryRefreshResponse["refreshResult"] | null
  >(null);
  // Truthful: process tools reach actors only when the env is running AND the
  // grant is on. (Previously inferred from status alone, which lied when the
  // grant was off.)
  const processToolsAvailable =
    environment?.status === "running" && environment.actorProcessToolsEnabled;
  const restartAndRefreshRepositories = useCallback(async () => {
    await restartWorkspaceAndRefreshRepositories(
      restart,
      refreshRepositoriesAfterRestart,
    );
  }, [refreshRepositoriesAfterRestart, restart]);
  // Detached rows are excluded so Attach can revive the stable tombstone through
  // the existing revival path; `detach_pending` stays included because that row
  // is still live and re-attaching it would race the owner node's removal.
  const attachedRepoKeys = useMemo(
    () =>
      new Set(
        repositories
          .filter((repository) => repository.status !== "detached")
          .map(
            (repository) =>
              `${repository.providerId}:${repository.repositoryId}`,
          ),
      ),
    [repositories],
  );
  const activeRepositories = useMemo(
    () => selectActiveRepositories(repositories),
    [repositories],
  );

  const closeDialog = () => {
    setDialog({ kind: "none" });
    setActionError(null);
    setActionErrorCause(null);
    setRefreshOverride(null);
  };

  const statusTone = getStatusTone(environment?.status ?? null);
  // A sandbox that never finished coming up (or errored/stopped) can be retried
  // — re-fires the node `start` for the existing env. `attaching` is included so
  // an env stuck part-way through attach (e.g. a transient node-dispatch error)
  // is recoverable in-place instead of needing kill+recreate.
  const envRetryable =
    environment !== null &&
    ["attaching", "errored", "stopped"].includes(environment.status);
  const summarySubtitle = environment
    ? `${formatRepoCount(activeRepositories.length)} · sandbox ${getStatusLabel(environment)}`
    : "No workspace attached";

  async function runAction(
    label: string,
    action: () => Promise<void>,
    { autoClose = true }: { autoClose?: boolean } = {},
  ) {
    setBusy(label);
    setActionError(null);
    setActionErrorCause(null);
    setRefreshResult(null);
    // Note: resumeRefusal is NOT cleared here — during the modal's own retry we
    // keep it mounted so the button can show a loading state; it clears on
    // success (below) or is replaced by a fresh refusal.
    try {
      await action();
      setResumeRefusal(null);
      setResumeRetry(null);
      setRefreshOverride(null);
      if (autoClose) {
        setDialog({ kind: "none" });
      }
    } catch (caughtError) {
      // A capacity refusal carries structured data — surface it as a modal
      // (deficit + held storage + suggested actions) rather than a flat notice,
      // and remember the failed action so Retry re-runs it.
      if (caughtError instanceof ResumeUnavailableError) {
        setResumeRefusal(caughtError.refusal);
        setResumeRetry({ label, run: action });
        setDialog({ kind: "none" });
      } else {
        const message =
          caughtError instanceof Error ? caughtError.message : "Action failed";
        setActionError(`${label}: ${message}`);
        setActionErrorCause(caughtError);
        setResumeRefusal(null);
        setResumeRetry(null);
      }
    } finally {
      setBusy(null);
    }
  }

  return {
    acknowledgeAbsentRepositoryDetach,
    actionError,
    actionErrorCause,
    activeRepositories,
    archive,
    attach,
    attachRepository,
    attachedRepoKeys,
    busy,
    changeRepositoryBranch,
    channel,
    clearActionError: () => {
      setActionError(null);
      setActionErrorCause(null);
    },
    clearResumeRefusal: () => {
      setResumeRefusal(null);
      setResumeRetry(null);
    },
    closeDialog,
    detach,
    detachRepository,
    dialog,
    envRetryable,
    environment,
    error,
    launchPreview,
    loading,
    openDialog: setDialog,
    patch,
    processToolsAvailable,
    profiles,
    profilesError,
    profilesLoading,
    refresh,
    refreshOverride,
    refreshRepositories,
    refreshRepository,
    refreshResult,
    repositories,
    repositoriesError,
    restart: restartAndRefreshRepositories,
    resumeRefusal,
    resumeRetry,
    runAction,
    setRefreshOverride,
    setRefreshResult,
    start,
    statusTone,
    summarySubtitle,
  };
}

export type WorkspaceController = ReturnType<typeof useWorkspaceController>;
